import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Button, Space, Tag, Input, Select, Row, Col, App } from 'antd';
import { SearchOutlined, EyeOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { formatDateBeijing } from '../utils/datetime';

const { Title, Text } = Typography;
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const EngineeringCases = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadCases = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
        search: searchText,
        type: filterType === 'all' ? '' : filterType,
      });

      const response = await fetch(`${API_BASE_URL}/industry/cases?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setCases(data.data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: data.total,
        });
      } else {
        message.error('加载案例数据失败');
      }
    } catch (error) {
      console.error('加载案例数据失败:', error);
      message.error('加载案例数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, [searchText, filterType]);

  const getTypeTag = (type) => {
    const colors = {
      '公路': 'blue',
      '铁路': 'green',
      '水利': 'cyan',
      '建筑': 'orange',
      '矿山': 'purple',
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  };

  const columns = [
    {
      title: '案例名称',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
    },
    {
      title: '工程类型',
      dataIndex: 'type',
      key: 'type',
      width: '12%',
      render: (type) => getTypeTag(type),
    },
    {
      title: '编制人',
      dataIndex: 'author',
      key: 'author',
      width: '15%',
    },
    {
      title: '发布时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (date) => formatDateBeijing(date),
    },
    {
      title: '操作',
      key: 'action',
      width: '23%',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small">
            预览
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} size="small">
            下载
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    loadCases(pagination.current, pagination.pageSize);
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>工程案例</Title>
        <Text type="secondary">各类工程水土保持方案案例库</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜索案例名称或编制人"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: '100%' }}
              placeholder="选择工程类型"
              value={filterType}
              onChange={setFilterType}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">全部类型</Option>
              <Option value="公路">公路工程</Option>
              <Option value="铁路">铁路工程</Option>
              <Option value="水利">水利工程</Option>
              <Option value="建筑">建筑工程</Option>
              <Option value="矿山">矿山工程</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={cases}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个案例`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default EngineeringCases;
